import { useState, useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Menu, User, Settings, LayoutDashboard, Circle, BookOpen, UserCheck, Shield, LogOut, Wine, FileText, Activity, RefreshCw, Trash2, EyeOff, Crown } from "lucide-react";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/theme/ThemeToggle";
import { useAdminRole } from "@/hooks/useAdminRole";
import { useCacheManager } from "@/hooks/useCacheManager";
import { useIncognitoBrowsing } from "@/hooks/useIncognitoBrowsing";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
const RightSideMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({
    x: window.innerWidth - 80,
    y: 24
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStarted, setDragStarted] = useState(false);
  const [dragOffset, setDragOffset] = useState({
    x: 0,
    y: 0
  });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const previousPathname = useRef(location.pathname);
  const {
    user
  } = useAuth();
  const {
    isAdmin,
    loading: adminLoading
  } = useAdminRole();
  const {
    isClearing,
    cacheInfo,
    clearAllCaches,
    forceUpdate,
    checkVersionMismatch,
    currentVersion
  } = useCacheManager();
  const {
    isIncognitoEnabled,
    hasIncognitoAccess,
    toggleIncognito,
    loading: incognitoLoading
  } = useIncognitoBrowsing();
  const handleIncognitoToggle = async (enabled: boolean) => {
    if (!hasIncognitoAccess) {
      toast({
        title: "Upgrade Required",
        description: "Incognito browsing is only available with Alpha∞ subscription",
        variant: "destructive"
      });
      navigate("/pricing");
      setIsOpen(false); // Close the menu when redirecting
      return;
    }
    const success = await toggleIncognito(enabled);
    if (success) {
      toast({
        title: enabled ? "Incognito Mode Enabled" : "Incognito Mode Disabled",
        description: enabled ? "Your profile views will now be anonymous" : "Your profile views will now show your identity"
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to update incognito setting",
        variant: "destructive"
      });
    }
  };

  // Simplified click and drag functionality
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // Only handle left clicks
    
    console.log('Mouse down event');
    e.preventDefault();
    
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setDragStart({ x: e.clientX, y: e.clientY });
      setIsDragging(false);
      setDragStarted(false); // Don't start tracking until threshold exceeded
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    const dragThreshold = 5;
    const deltaX = Math.abs(e.clientX - dragStart.x);
    const deltaY = Math.abs(e.clientY - dragStart.y);

    // Only start tracking if we exceed threshold
    if (!dragStarted && (deltaX > dragThreshold || deltaY > dragThreshold)) {
      console.log('Starting drag tracking');
      setDragStarted(true);
      setIsDragging(true);
    }

    if (isDragging && dragStarted) {
      e.preventDefault();
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      const maxX = window.innerWidth - 56;
      const maxY = window.innerHeight - 56;

      setButtonPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    }
  };

  const handleMouseUp = () => {
    console.log('Mouse up - dragStarted:', dragStarted, 'isDragging:', isDragging);
    
    if (dragStarted && isDragging) {
      console.log('Ending drag - NOT toggling menu');
    } else {
      console.log('Simple click - toggling menu from', isOpen, 'to', !isOpen);
      setIsOpen(prev => !prev);
    }
    
    // Always reset states
    setIsDragging(false);
    setDragStarted(false);
  };

  // Simplified touch handlers for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      });
      setDragStart({ x: touch.clientX, y: touch.clientY });
      setIsDragging(false); // Reset drag state
      setDragStarted(true); // Track that user started interaction
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!dragStarted) return; // Only process if user started interaction
    
    const dragThreshold = 8; // Increased threshold
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - dragStart.x);
    const deltaY = Math.abs(touch.clientY - dragStart.y);
    
    if (!isDragging && (deltaX > dragThreshold || deltaY > dragThreshold)) {
      setIsDragging(true);
    }
    
    if (isDragging) {
      e.preventDefault();
      const newX = touch.clientX - dragOffset.x;
      const newY = touch.clientY - dragOffset.y;
      const maxX = window.innerWidth - 56;
      const maxY = window.innerHeight - 56;
      setButtonPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    }
  };

  const handleTouchEnd = () => {
    if (isDragging) {
      // Was dragging, don't toggle menu
    } else {
      setIsOpen(!isOpen);
    }
    // Reset all drag states
    setIsDragging(false);
    setDragStarted(false);
  };
  const adminSection = {
    title: "Administration",
    items: [{
      title: "Content Moderation",
      path: "/admin/content-moderation",
      icon: Shield,
      description: "Content moderation panel"
    }, {
      title: "System Monitoring",
      path: "/admin/monitoring",
      icon: Activity,
      description: "Logs and system status"
    }]
  };
  const menuClusters = [{
    title: "Profile & Settings",
    items: [{
      title: "Preview My Profile",
      path: user ? `/gprofile/${user.id}` : "/gprofile",
      icon: User,
      description: "See your profile as others do"
    }, {
      title: "Edit My Profile",
      path: "/complete-profile",
      icon: UserCheck,
      description: "Update your profile information"
    }, {
      title: "Settings",
      path: "/settings",
      icon: Settings,
      description: "App preferences"
    }, {
      title: "Block List",
      path: "/block-list",
      icon: Shield,
      description: "Manage blocked users"
    }]
  }, {
    title: "Navigation & Utilities",
    items: [{
      title: "Stories",
      path: "/stories",
      icon: Circle,
      description: "Social content"
    }, {
      title: "Private Events",
      path: "/events",
      icon: Wine,
      description: "Exclusive gatherings"
    }, {
      title: "Kinkipedia",
      path: "/kinkipedia",
      icon: BookOpen,
      description: "Knowledge base"
    }]
  }, ...(isAdmin ? [adminSection] : []), {
    title: "Legal & Compliance",
    items: [{
      title: "Terms of Service",
      path: "/terms-of-service",
      icon: FileText,
      description: "Our terms and conditions"
    }, {
      title: "Privacy Policy",
      path: "/privacy-policy",
      icon: Shield,
      description: "How we protect your data"
    }]
  }, {
    title: "Account",
    items: [{
      title: "Sign Out",
      path: "/sign-out",
      icon: LogOut,
      description: "Sign out of your account"
    }]
  }];
  const handleItemClick = () => {
    setIsOpen(false);
  };
  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  // Auto-close menu on route change
  useEffect(() => {
    if (location.pathname !== previousPathname.current) {
      setIsOpen(false);
      previousPathname.current = location.pathname;
    }
  }, [location.pathname]);

  // Auto-close menu on escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen]);

  // Auto-close menu on click outside (enhanced)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && menuRef.current && !menuRef.current.contains(event.target as Node) && !(event.target as HTMLElement).closest('[data-floating-button]')) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Auto-close menu on main content interaction
  useEffect(() => {
    const handleMainContentInteraction = (event: Event) => {
      const target = event.target as HTMLElement;
      const isMenuRelated = target.closest('[data-menu-related]') || target.closest('[data-floating-button]') || menuRef.current?.contains(target);
      if (isOpen && !isMenuRelated) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('click', handleMainContentInteraction, true);
      document.addEventListener('touchstart', handleMainContentInteraction, true);
      return () => {
        document.removeEventListener('click', handleMainContentInteraction, true);
        document.removeEventListener('touchstart', handleMainContentInteraction, true);
      };
    }
  }, [isOpen]);

  // Global event listeners for drag functionality - only when actually needed
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => handleMouseMove(e);
    const handleGlobalMouseUp = () => handleMouseUp();
    const handleGlobalTouchMove = (e: TouchEvent) => handleTouchMove(e);
    const handleGlobalTouchEnd = () => handleTouchEnd();

    // Always add mouse up listener to catch button release
    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('touchend', handleGlobalTouchEnd);

    // Only add move listeners if we have a valid drag start position
    if (dragStart.x !== 0 || dragStart.y !== 0) {
      document.addEventListener('mousemove', handleGlobalMouseMove, { passive: false });
      document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, [dragStart.x, dragStart.y]);

  // Update button position on window resize
  useEffect(() => {
    const handleResize = () => {
      const maxX = window.innerWidth - 56;
      const maxY = window.innerHeight - 56;
      setButtonPosition(prev => ({
        x: Math.max(0, Math.min(prev.x, maxX)),
        y: Math.max(0, Math.min(prev.y, maxY))
      }));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  if (!user) {
    return null;
  }
  return <>
      {/* Custom Overlay with explicit z-index */}
      {isOpen && <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm" onClick={() => setIsOpen(false)} style={{
      isolation: 'isolate'
    }} />}
      
      {/* Floating Action Button */}
      <Button ref={buttonRef} variant="ghost" size="icon" onMouseDown={handleMouseDown} onTouchStart={handleTouchStart} data-floating-button="true" className={cn("fixed z-[60] w-14 h-14 rounded-full transition-all duration-300 ease-out", "bg-primary text-primary-foreground hover:bg-primary/90", "shadow-lg hover:shadow-xl dark:shadow-primary/20 hover:dark:shadow-primary/30", "shadow-neon-red hover:shadow-neon-red", "border border-border/20 backdrop-blur-sm", "hover:scale-105 active:scale-95", "flex items-center justify-center pointer-events-auto cursor-move", isDragging && "cursor-grabbing scale-110")} style={{
      left: `${buttonPosition.x}px`,
      top: `${buttonPosition.y}px`,
      userSelect: 'none'
    }}>
        <img 
          src="/lovable-uploads/982f1f6a-f48a-4038-8d62-d211631afd5a.png" 
          alt="Menu" 
          className="w-8 h-8 object-cover"
        />
      </Button>

      {/* Custom Sheet Content */}
      <div ref={menuRef} data-menu-related="true" className={`fixed inset-y-0 right-0 z-[105] w-96 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`} style={{
      isolation: 'isolate',
      transform: isOpen ? 'translateZ(0)' : 'translateX(100%) translateZ(0)',
      willChange: 'transform'
    }}>
        <div className="h-full p-0 border-l border-steel-silver/20 backdrop-blur-xl font-tt-supermolot bg-anthracite text-white relative overflow-hidden">
          {/* Background Logo */}
          <div className="absolute inset-0 opacity-30 bg-contain bg-center bg-no-repeat z-10" style={{
          backgroundImage: "url('/lovable-uploads/8497e2de-4a35-4f10-9960-e39f4cc2988e.png')"
        }} />
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-anthracite/85 via-anthracite/80 to-black/85 z-0" />
          
          {/* Close Button */}
          <button onClick={() => setIsOpen(false)} className="absolute right-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 object-fill">
            <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span className="sr-only">Close</span>
          </button>
          
          <div className="p-6 pb-0 relative z-10">
            <div className="text-left text-xl font-bold text-white flex items-center mx-[55px]">
              <img alt="ALPHA Logo" className="w-8 h-8 mr-3 rounded-lg" src="/lovable-uploads/da82ed0f-b4ae-4046-b1d8-01a1fb99c462.png" />
              ALPHA Navigation
            </div>
          </div>
          
          <ScrollArea className="h-[calc(100vh-120px)] px-6 relative z-10">
            <div className="mt-6 space-y-6 pb-6">
              {/* Theme Toggle Section */}
              <div>
                <h3 className="text-sm font-semibold text-steel-silver uppercase tracking-wide mb-3 px-1">
                  Appearance
                </h3>
                <div className="p-3 bg-black rounded-lg border border-steel-silver/20 backdrop-blur-sm">
                  <ThemeToggle />
                </div>
              </div>

              {/* Cache Management Section */}
              <div>
                <h3 className="text-sm font-semibold text-steel-silver uppercase tracking-wide mb-3 px-1">
                  Cache Management
                </h3>
                <div className="space-y-2">
                  <div className="p-3 bg-black rounded-lg border border-steel-silver/20 backdrop-blur-sm">
                    <div className="text-xs text-steel-silver mb-2">
                      Version: {currentVersion}
                      {cacheInfo?.isSamsung && <span className="text-alpha-orange ml-2">Samsung Device</span>}
                      {checkVersionMismatch() && <span className="text-red-400 ml-2">⚠️ Outdated</span>}
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={forceUpdate} size="sm" variant="outline" className="flex-1 text-xs" disabled={isClearing}>
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Force Update
                      </Button>
                      <Button onClick={clearAllCaches} size="sm" variant="outline" className="flex-1 text-xs" disabled={isClearing}>
                        <Trash2 className="w-3 h-3 mr-1" />
                        {isClearing ? 'Clearing...' : 'Clear Cache'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Incognito Browsing Section */}
              <div>
                <h3 className="text-sm font-semibold text-steel-silver uppercase tracking-wide mb-3 px-1">
                  Privacy Mode
                </h3>
                <div className="p-3 bg-black rounded-lg border border-steel-silver/20 backdrop-blur-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <EyeOff className="w-4 h-4 text-steel-silver" />
                      <span className="text-sm text-white">Incognito</span>
                      {!hasIncognitoAccess && <Crown className="w-3 h-3 text-alpha-orange" />}
                    </div>
                    {hasIncognitoAccess ? <Switch checked={isIncognitoEnabled} onCheckedChange={handleIncognitoToggle} disabled={incognitoLoading} /> : <Button variant="outline" size="sm" onClick={() => {
                    navigate("/pricing");
                    setIsOpen(false);
                  }} className="border-alpha-orange text-alpha-orange hover:bg-alpha-orange hover:text-black text-xs px-2 py-1 h-6">
                        Upgrade
                      </Button>}
                  </div>
                  <p className="text-xs text-steel-silver mt-2">
                    {hasIncognitoAccess ? isIncognitoEnabled ? "Browsing anonymously" : "Browse profiles anonymously" : "Alpha∞ feature"}
                  </p>
                </div>
              </div>

              <Separator className="bg-steel-silver/20" />

              {menuClusters.map((cluster, clusterIndex) => <div key={cluster.title}>
                  <h3 className="text-sm font-semibold text-steel-silver uppercase tracking-wide mb-3 px-1">
                    {cluster.title}
                  </h3>
                  <div className="space-y-2">
                    {cluster.items.map(item => {
                  const Icon = item.icon;
                  const isActive = isActiveRoute(item.path);
                  return <Link key={item.path} to={item.path} onClick={handleItemClick} className={cn("flex items-center space-x-3 px-6 py-3 rounded-lg transition-all duration-300 group border text-base", isActive ? "bg-alpha-orange text-black border-alpha-orange shadow-alpha-orange/30" : "text-white/80 hover:bg-black/50 hover:text-white border-transparent hover:border-alpha-orange/30")}>
                          <Icon className={cn("w-5 h-5 transition-colors", isActive ? "text-black" : "text-steel-silver group-hover:text-white")} />
                          <div className="flex-1">
                            <div className={cn("font-medium text-base", isActive ? "text-black" : "text-white")}>
                              {item.title}
                            </div>
                            <div className={cn("text-sm", isActive ? "text-black/70" : "text-steel-silver")}>
                              {item.description}
                            </div>
                          </div>
                          {isActive && <div className="w-2 h-2 bg-black rounded-full"></div>}
                        </Link>;
                })}
                  </div>
                  {clusterIndex < menuClusters.length - 1 && <Separator className="mt-4 bg-steel-silver/20" />}
                </div>)}
            </div>
          </ScrollArea>
        </div>
      </div>
    </>;
};
export default RightSideMenu;